import React, { SetStateAction, useContext, useRef, useState } from "react";
import {
  BackIcon,
  DarkModeIcon,
  EmailIcon,
  LightModeIcon,
  PasswordIcon,
  UsernameIcon,
} from "../../components/iconSet/IconSet";
import { ThemeTypes } from "../../context/ContextTypes";

import logoIcon from "../../images/icons/logo.png";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  APICall,
  DATA_VALIDATION_ERROR,
  USER_CREDENTIALS_ERROR,
} from "../../services/APIService";
import {
  API_LOGIN_URL,
  APILoginType,
} from "../../services/APIAuthorizationService";
import { useTheme } from "../../context/ThemeContext";
import { generatePKCE } from "../../utils/keycloakPKCEUtils";
import AlertDialog from "../../components/popupComp/AlertDialog";
import axios from "axios";

const ResetForgotPassword = () => {
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const { theme, setTheme } = useTheme();

  const usernameRef = useRef<HTMLInputElement | null>(null);
  const passwordRef = useRef<HTMLInputElement | null>(null)
  const confirmPasswordRef = useRef<HTMLInputElement | null>(null)

  const [alertDialogState, setAlertDialogState] = useState(false);
    const [alertTitle, setAlertTitle] = useState('');
    const [alertDescription, setAlertDescription] = useState('');
    const [alertYesCallback, setAlertYesCallback] = useState<(() => void) | undefined>(undefined); // for Yes
    const [alertOkCallback, setAlertOkCallback] = useState<(() => void) | undefined>(undefined);  // for OK
  
    const showAlertDialog = (
    title: string,
    description: string,
    yesCallback?: () => void,
    okCallback?: () => void
    ) => {
    setAlertTitle(title);
    setAlertDescription(description);
    setAlertYesCallback(() => yesCallback); // Will show Yes/Cancel if defined
    setAlertOkCallback(() => okCallback);   // Will trigger only on OK
    setAlertDialogState(true);
    };

  const [status, setStatus] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    const newPassword = passwordRef.current?.value.trim();
    const confirmPassword = confirmPasswordRef.current?.value.trim();

    if (!newPassword || !confirmPassword) {
      showAlertDialog('Validation Error', 'Please fill in both fields.');
      return;
    }

    if (newPassword !== confirmPassword) {
      showAlertDialog('Mismatch', 'Passwords do not match.');
      return;
    }

    if (!token) {
      showAlertDialog('Error', 'Missing or invalid token.');
      return;
    }

    try {
      await axios.put(
        `${BASE_URL}/jwt-auth/api/v1/reset-forgot-password?token=${token}`,
        { newPassword }, // Only send newPassword
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      showAlertDialog('Success', '✅ Password reset successfully.', undefined, () => navigate('/login'));
    } catch (error: any) {
      const message = error.response?.data?.message || error.response?.data || 'Failed to reset password.';
      showAlertDialog('Error', message);
    }
  };

  const handleThemeChangeClick = (type: ThemeTypes) => {
    setTheme((prevState) => type);
  };

  return (
    <div className={`login-page-wrapper ${theme}`}>
      <div className={`login-container`}>
        <div className={`form-area`}>
          <span className={`title`}>Reset Your Password</span>
          <div className={`input-wrapper`}>
            <i>
              <PasswordIcon color="var(--color-main-white)" width="1.2rem" />
            </i>
            <input
              type="password"
              placeholder="Password" ref={passwordRef}
            />
          </div>
          <div className={`input-wrapper`}>
            <i>
              <PasswordIcon color="var(--color-main-white)" width="1.2rem" />
            </i>
            <input
              type="password"
              placeholder="Confirm Password" ref={confirmPasswordRef}
            />
          </div>
          <div className={`button-wrapper`}>
            <button
              className={`button-full`}
              onClick={(e) =>
									showAlertDialog(
										"Confirm Password Reset",
										"Are you sure you want to reset your password?",
										() => handleResetPassword(e),
										undefined
									) }
            >
              Reset
            </button>
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "0.3rem",
              marginTop: "1.0rem",
              cursor: "pointer",
              fontSize: "0.5rem",
              color: "var(--color-main-black)",
              fontWeight: "bold",
              transition: "opacity 0.3s ease",
            }}
            onClick={() => navigate("/login")}
            onMouseOver={(e) => {
              e.currentTarget.style.opacity = "0.7";
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.opacity = "1";
            }}
          >
            <BackIcon width="0.8rem" color="var(--color-main-black)" />
            <span style={{ fontSize: "0.8rem", fontWeight: "bold" }}>
              Back to Login
            </span>
          </div>
        </div>
        <div className={`logo-area`}>
          <img className={`logo-image`} src={logoIcon} alt="" />
          <span className="title">BCONX</span>
          <span className="water-mark">Powered by Tecxal Systems</span>
        </div>

        {theme === "dark" ? (
          <div
            className={`theme-button-wrapper`}
            onClick={(e) => handleThemeChangeClick("light")}
          >
            <LightModeIcon color="white" width="2rem" />
          </div>
        ) : theme === "light" ? (
          <div
            className={`theme-button-wrapper`}
            onClick={(e) => handleThemeChangeClick("dark")}
          >
            <DarkModeIcon color="white" width="1rem" />
          </div>
        ) : null}
      </div>
      {alertDialogState && (
        <AlertDialog
          title={alertTitle}
          description={alertDescription}
          windowState={alertDialogState}
          setWindowState={setAlertDialogState}
          function={alertYesCallback}
          okFunction={alertOkCallback}
      />
      )}
    </div>
  );
};

export default ResetForgotPassword;
